import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { apiService } from '@/services/api'

const blogTagSchema = z.object({
  name: z.string().min(1, 'Name is required').max(50, 'Name must be less than 50 characters'),
  slug: z.string()
    .min(1, 'Slug is required')
    .max(50, 'Slug must be less than 50 characters')
    .regex(/^[a-z0-9-]+$/, 'Slug must only contain lowercase letters, numbers, and hyphens'),
})

export type BlogTagFormData = z.infer<typeof blogTagSchema>

interface BlogTagFormProps {
  tag?: {
    id: string
    name: string
    slug: string
  }
  onSuccess?: () => void
  onCancel?: () => void
}

function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim()
}

export function BlogTagForm({ tag, onSuccess, onCancel }: BlogTagFormProps) {
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const form = useForm<BlogTagFormData>({
    resolver: zodResolver(blogTagSchema),
    defaultValues: {
      name: tag?.name || '',
      slug: tag?.slug || '',
    },
  })

  const nameValue = form.watch('name')

  useEffect(() => {
    if (!tag && nameValue) {
      const generatedSlug = generateSlug(nameValue)
      form.setValue('slug', generatedSlug, { shouldValidate: false })
    }
  }, [nameValue, tag, form])

  async function onSubmit(data: BlogTagFormData) {
    try {
      setIsSubmitting(true)

      if (tag) {
        await apiService.blog.tags.update(tag.id, data)
        toast.success('Tag updated successfully')
      } else {
        await apiService.blog.tags.create(data)
        toast.success('Tag created successfully')
      }

      form.reset()
      onSuccess?.()
    } catch (error) {
      console.error('Failed to save tag:', error)
      toast.error(tag ? 'Failed to update tag' : 'Failed to create tag')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="e.g., Technology"
                  disabled={isSubmitting}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="slug"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Slug</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="e.g., technology"
                  disabled={isSubmitting}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex gap-2 justify-end">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          )}
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {tag ? 'Update Tag' : 'Create Tag'}
          </Button>
        </div>
      </form>
    </Form>
  )
}